import {Command, Extension, GlobalAttributes} from '@tiptap/core';
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {erIkkeTomCellSelection} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/tiptapHjelpere";
import {nil} from "felles-ng2/typer/nullish";

/** Tiptap utvidelse som legger til attributtet 'bakgrunnsfarge' for bruk på tabell-celler (tableCell og tableHead) */
export const TabellcelleBakgrunn = Extension.create({

    name: 'TabellcelleBakgrunn',

    /** Legger til bakgrunnsfarge-attributtet på både 'tableCell' og 'tableHeader', bruker derfor addGlobalAttributes */
    addGlobalAttributes(): GlobalAttributes {
        return [{
            types: tillatteNodetyper(),
            attributes: {
                bakgrunnsfarge: {
                    default: null,
                    renderHTML: attributter => tabellcelleBakgrunnSomHtmlStyleAttributt(attributter),
                    parseHTML: element => htmlBorderStyleSomRammeAttributt(element),
                    keepOnSplit: true,
                }
            }
        }];
    },

    addCommands(): TabellcelleBakgrunnKommandoer<Command> {
        return {
            setCelleBakgrunnsfarge: (bakgrunnsfarge) => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {bakgrunnsfarge: bakgrunnsfarge}), props.chain())
                  .run();
            },

            fjernCelleBakgrunnsfarge: () => props => {
                if (erNil(props.dispatch)) return erIkkeTomCellSelection(props.tr.selection); //kommandoen kalles i dry-run-modus
                return tillatteNodetyper().reduce((chain, type) =>
                  chain.updateAttributes(type, {bakgrunnsfarge: null}), props.chain())
                  .run();
            }
        };
    }
});

function tillatteNodetyper() {
    return [NodeType.TableCell, NodeType.TableHeader];
}

/** Style-attributter som administreres av {@link TabellcelleBakgrunn}.
 * Vi leser bakgrunnsfarge via enten 'background' eller 'background-color', men vi skriver alltid om til å angi bakgrunnsfarge via 'background-color' */
export const styleAttributterForBakgrunn = ['background', 'background-color']

/** Serialiserer 'bakgrunnsfarge' attributtet til html css style background-color attributt */
export function tabellcelleBakgrunnSomHtmlStyleAttributt(attributter: Record<string, any>): Record<string, any> {
    const cssFragment = somCssStyle(attributter.bakgrunnsfarge);
    return erIkkeNil(cssFragment) ? {style: cssFragment} : {};
}

function somCssStyle(bakgrunnsfarge: string | nil) {
    if (erTomEllerNil(bakgrunnsfarge)) return null;
    return styleAttributterForBakgrunn
      .filter(attributt => attributt != 'background') //vi fjerner background når vi genererer css-styling
      .map(attributt => `${attributt}:${bakgrunnsfarge}`).join(';')
}

/** Deserialiserer css style.backgroundColor / style.background property satt på angitt element til bakgrunnsfarge */
function htmlBorderStyleSomRammeAttributt(element: HTMLElement): string {
    return element.style.backgroundColor || element.style.background;
}


// typedefinisjoner

interface TabellcelleBakgrunnKommandoer<ReturnType> {
    setCelleBakgrunnsfarge: (bakgrunnsfarge: string) => ReturnType;
    fjernCelleBakgrunnsfarge: () => ReturnType;
}

//for at kommandoene skal dukke opp i editor ts typen
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        celleBakgrunnsfarge: TabellcelleBakgrunnKommandoer<ReturnType>;
    }
}
